import clsx from "clsx";
import { type FC, ReactNode, useCallback } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import { SectionUserOptionsMenu } from "../home/admin/users/AdminUserOptionsMenu";

import { userActivityIcons } from "./helpers";

import { SectionUserType, UserType } from "components/server-types";
import { t } from "i18n/i18n";
import { ListItem } from "mds/components/ListItem";
import { showConfirmationModal } from "mds/components/Modal";
import { Tooltip } from "mds/components/Tooltip";
import {
  HandRaiseCircleIcon,
  PresentPlayBackgroundIcon,
  PresentPlayIcon,
  ReactionVoteNoIcon,
  ReactionVoteYesIcon,
  ScreenshareIcon,
} from "mds/icons";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  SectionUserActivityStatus,
  selectActivityStatusForUser,
  selectClassUserByUserId,
  selectCurrentCourseCode,
  selectCurrentSectionTitle,
  selectFollowingStatusForUser,
  selectSectionUserByUserId,
  userSelectors,
} from "store/selectors";
import {
  selectParticipantBarFeaturedClassUserIds,
  selectParticipantBarVisibleClassUserIds,
} from "store/selectors/pubsub-virtual-class-state";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { useAv } from "utils/av";
import { REACTION_TO_ICON } from "utils/reactions";
import { getSectionResourceRole } from "utils/section";
import { createImpersonationUrl, getUserFullName } from "utils/user-utils";

type EnhancedSectionUserActivityStatus =
  | SectionUserActivityStatus
  | "disabled"
  | "following"
  | "presenting"
  | "screensharing";

const enhancedUserActivityIcons: Record<EnhancedSectionUserActivityStatus, ReactNode> = {
  ...userActivityIcons,
  following: <PresentPlayIcon />,
  presenting: (
    <span className="text-green">
      <PresentPlayBackgroundIcon />
    </span>
  ),
  screensharing: (
    <span className="text-green">
      <ScreenshareIcon size="m" />
    </span>
  ),
};

const statusTooltipText: Record<EnhancedSectionUserActivityStatus, string> = {
  active: "",
  inactive: "",
  offline: "",
  disabled: "",
  following: t("presentation.following"),
  presenting: t("presentation.presenting"),
  screensharing: t("av.screenshare.banner.screensharing"),
};

const handleDisableUser = (
  user: UserType,
  sectionUser: SectionUserType,
  sectionTitle: string | null,
  courseCode: string | null,
) => {
  const resourceRole = getSectionResourceRole(sectionUser.role);
  const role = t(`roles.section_${resourceRole}`);
  const name = getUserFullName(user);
  const sectionName = t("common.course_code_and_title", {
    code: courseCode,
    title: sectionTitle,
  });

  const modalAction = sectionUser.is_disabled ? "grant" : "restrict";

  return showConfirmationModal({
    title: t(`user_options_menu.role.${modalAction}_title`, { role }),
    labelText: t(`user_options_menu.role.${modalAction}_label`, {
      name,
      course: sectionName,
    }),
    confirmButtonText: t(`user_options_menu.role.${modalAction}_button_text`),
    confirmButtonKind: sectionUser.is_disabled ? "accent" : "caution",
    onConfirm: () => {
      storeApi.section_users.partial_update({
        id: sectionUser.id,
        is_disabled: !sectionUser.is_disabled,
      });
    },
  });
};

interface SectionUserItemProps {
  userId: string;
  canImpersonate: boolean;
  canModifyUsers: boolean;
  presentingUserId?: string | null;
  screensharingUserId?: string | null;
}

export const SectionUserItem: FC<SectionUserItemProps> = ({
  userId,
  canImpersonate,
  canModifyUsers,
  presentingUserId,
  screensharingUserId,
}) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const user = useAppSelector((state) => userSelectors.selectById(state, userId));
  const sectionActivityStatus = useAppSelector((state) =>
    selectActivityStatusForUser(state, userId),
  );
  const isFollowing = useAppSelector((state) => selectFollowingStatusForUser(state, userId));
  const sectionUser = useAppSelector((state) => selectSectionUserByUserId(state, userId));
  const classUser = useAppSelector((state) => selectClassUserByUserId(state, userId));
  const currentSectionTitle = useAppSelector(selectCurrentSectionTitle);
  const currentCourseCode = useAppSelector(selectCurrentCourseCode);
  const { isInVirtualClass } = useAv();
  const dispatch = useAppDispatch();

  const currentFeaturedClassUserIds = useAppSelector(selectParticipantBarFeaturedClassUserIds);
  const participantBarVisibleClassUserIds = useAppSelector(selectParticipantBarVisibleClassUserIds);

  const onFeatureParticipant = useCallback(() => {
    if (!classUser?.id) return;

    const isCurrentlyFeatured = currentFeaturedClassUserIds.includes(classUser.id);
    if (isCurrentlyFeatured) return;
    const newFeaturedClassUserIds = [classUser.id, ...currentFeaturedClassUserIds];

    dispatch(
      PubsubVirtualClassStateActions.setParticipantBarFeaturedClassUserIds(newFeaturedClassUserIds),
    );
  }, [dispatch, classUser?.id, currentFeaturedClassUserIds]);

  if (!user || !sectionUser) return null;

  const isParticipantVisibleInBar =
    classUser != null && participantBarVisibleClassUserIds.includes(classUser.id);

  const isPresenting = userId === presentingUserId;
  const isScreensharing = userId === screensharingUserId;
  const status = sectionUser.is_disabled
    ? "disabled"
    : isScreensharing
      ? "screensharing"
      : isPresenting
        ? "presenting"
        : isFollowing
          ? "following"
          : sectionActivityStatus;

  const ReactionIcon = classUser?.reaction && REACTION_TO_ICON[classUser.reaction];

  return (
    <ListItem
      className={clsx("flex items-center justify-between gap-2 text-sm", {
        offline:
          sectionActivityStatus === SectionUserActivityStatus.OFFLINE && !sectionUser.is_disabled,
      })}
      key={userId}
    >
      <div className="flex min-w-0 flex-row items-center gap-2" title={user.email}>
        {ReactionIcon ? (
          <ReactionIcon />
        ) : (
          <Tooltip content={statusTooltipText[status]}>{enhancedUserActivityIcons[status]}</Tooltip>
        )}
        <span className={clsx("truncate", { "text-black-tint-55": sectionUser.is_disabled })}>
          {getUserFullName(user)}
        </span>
      </div>
      <div className="flex flex-row items-center gap-1">
        {classUser?.hand_raised_at && <HandRaiseCircleIcon aria-label={t("av.raised_hand")} />}
        {classUser?.vote === "yes" && <ReactionVoteYesIcon aria-label={t("reactions.vote_yes")} />}
        {classUser?.vote === "no" && <ReactionVoteNoIcon aria-label={t("reactions.vote_no")} />}
        {canModifyUsers && (
          <SectionUserOptionsMenu
            featureParticipantDisabled={isParticipantVisibleInBar}
            sectionUser={sectionUser}
            user={user}
            onChangeRole={(newRole) => {
              storeApi.section_users
                .partial_update({
                  id: sectionUser.id,
                  role: newRole,
                })
                .catch(
                  toastLocalizedOperationCatcher("update_failure", {
                    item: t("glossary.user_role"),
                  }),
                );
            }}
            onClickDisable={() =>
              handleDisableUser(user, sectionUser, currentSectionTitle, currentCourseCode)
            }
            onFeatureParticipant={isInVirtualClass ? onFeatureParticipant : undefined}
            onImpersonate={
              canImpersonate
                ? () => {
                    createImpersonationUrl(user.id).then(copyToClipboard);
                  }
                : undefined
            }
          />
        )}
      </div>
    </ListItem>
  );
};
